import ContractTitle from '@/routes/authenticated/billing/contracts/contract/ContractTitle';
import api from '@/services/ApiService';
import { ArrowBack, DocumentScanner } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams, useRouteLoaderData } from 'react-router-dom';
import ContractContent from './ContractContent';
import { toast } from 'react-toastify';
import proxy from '@/services/ProxyService';
import calculateAndEnrichItems from './content/calculateAndEnrichItems';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useCallback } from 'react';
import { fetchWeclappPrice } from './content/fetchWeclappPrice';
import useTitle from '@/hooks/useTitle';
import { useNavigate } from 'react-router-dom';
import { set } from 'lodash';
import HoldButton from '@/components/HoldButton';

const fetchAndApplyPricesForBomItem = async (
  bomItem,
  customerId,
  config,
  fetchWeclappPrice,
) => {
  if (
    !bomItem ||
    bomItem.articleType !== 'SALES_BILL_OF_MATERIAL' ||
    !customerId ||
    !config?.interfaces?.weclapp
  ) {
    return bomItem;
  }

  const updatedItem = { ...bomItem };

  const { price: mainPrice } = await fetchWeclappPrice({
    articleId: updatedItem.articleId,
    customerId: customerId,
    pricingDate: updatedItem.pricingDate
      ? new Date(updatedItem.pricingDate)
      : new Date(),
    quantity:
      updatedItem.type === 'STATIC'
        ? updatedItem.quantity || 0
        : updatedItem.quantity?.min || 0,
    weclappApiUrl: config.interfaces.weclapp,
  }).catch(() => null);

  updatedItem.unchangedUnitPrice = mainPrice !== null ? Number(mainPrice) : 0;
  let finalTotalUnitPrice = updatedItem.unchangedUnitPrice;

  if (updatedItem.salesBillOfMaterialItems) {
    const subItemUpdatePromises = updatedItem.salesBillOfMaterialItems.map(
      async (subItem) => {
        const { price: subPrice } = await fetchWeclappPrice({
          articleId: subItem.articleId,
          customerId: customerId,
          pricingDate: updatedItem.pricingDate
            ? new Date(updatedItem.pricingDate)
            : new Date(),
          quantity:
            updatedItem.type === 'STATIC'
              ? updatedItem.quantity || 0
              : updatedItem.quantity?.min || 0,
          weclappApiUrl: config.interfaces.weclapp,
        }).catch(() => null);

        const updatedSubItem =
          subPrice !== null
            ? { ...subItem, unitPrice: subPrice }
            : { ...subItem, unitPrice: 0 };

        if (updatedItem.useSalesBillOfMaterialItemPrices !== false) {
          const subItemFactor = subItem.articleFactor || 1;
          finalTotalUnitPrice +=
            (updatedSubItem.unitPrice || 0) * subItemFactor;
        }

        return updatedSubItem;
      },
    );
    updatedItem.salesBillOfMaterialItems = await Promise.all(
      subItemUpdatePromises,
    );
  }

  updatedItem.unitPrice = finalTotalUnitPrice;
  return updatedItem;
};

export default function ContractPage() {
  const { id } = useParams();
  useTitle(`Vertrag`);
  const {
    fixedDeploymentConfigs: { contracts },
  } = useRouteLoaderData('auth');
  const [party, setParty] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [datasource, setDatasource] = useState(null);
  const [datasources, setDatasources] = useState([]);
  const [contract, setContract] = useState(null);
  const [contractKey, setContractKey] = useState(null);
  const [allContracts, setAllContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  const [coverageStatus, setCoverageStatus] = useState({
    status: 'loading',
    messages: [],
  });
  const [showInvoiceWarning, setShowInvoiceWarning] = useState(null);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] =
    useState(false);
  const navigator = useNavigate();

  const currentSource = useMemo(() => {
    if (!datasource || datasources.length === 0) {
      return [];
    }
    return (
      datasources.find((d) => d._id === datasource)?.lastQueryResult
        ?.articles || []
    );
  }, [datasource, datasources]);

  const fetchAndApplyWeclappPrices = useCallback(
    async (contractData, weclappCustomerId) => {
      if (
        !contractData?.contractItems ||
        !weclappCustomerId ||
        !contracts?.interfaces?.weclapp
      ) {
        return contractData;
      }
      setIsPricingLoading(true);

      const updatedItemsPromises = contractData.contractItems.map(
        async (item) => {
          const updatedItem = { ...item };

          const { price: mainPrice } = await fetchWeclappPrice({
            articleId: item.articleId,
            customerId: weclappCustomerId,
            pricingDate: item.pricingDate
              ? new Date(item.pricingDate)
              : new Date(contractData.contractDate),
            quantity:
              item.type === 'STATIC'
                ? item.quantity || 0
                : item.quantity?.min || 0,
            weclappApiUrl: contracts.interfaces.weclapp,
          }).catch(() => null);

          let finalTotalUnitPrice = mainPrice !== null ? Number(mainPrice) : 0;
          if (
            item.articleType === 'SALES_BILL_OF_MATERIAL' &&
            item.salesBillOfMaterialItems
          ) {
            const subItemUpdatePromises = item.salesBillOfMaterialItems.map(
              async (subItem) => {
                const effectivePricingDate = subItem.pricingDate
                  ? new Date(subItem.pricingDate)
                  : item.pricingDate
                    ? new Date(item.pricingDate)
                    : new Date(contractData.contractDate);
                const { price: subPrice } = await fetchWeclappPrice({
                  articleId: subItem.articleId,
                  customerId: weclappCustomerId,
                  pricingDate: effectivePricingDate,
                  quantity:
                    item.type === 'STATIC'
                      ? item.quantity || 0
                      : item.quantity?.min || 0,
                  weclappApiUrl: contracts.interfaces.weclapp,
                }).catch(() => null);
                const updatedSubItem =
                  subPrice !== null
                    ? { ...subItem, unitPrice: subPrice }
                    : { ...subItem, unitPrice: 0 };
                if (item.useSalesBillOfMaterialItemPrices !== false) {
                  const subItemFactor = subItem.articleFactor || 1;
                  finalTotalUnitPrice +=
                    (updatedSubItem.unitPrice || 0) * subItemFactor;
                }

                return updatedSubItem;
              },
            );
            updatedItem.salesBillOfMaterialItems = await Promise.all(
              subItemUpdatePromises,
            );
          }

          updatedItem.unitPrice = finalTotalUnitPrice;
          return updatedItem;
        },
      );

      const updatedItems = await Promise.all(updatedItemsPromises);
      setIsPricingLoading(false);
      return { ...contractData, contractItems: updatedItems };
    },
    [contracts],
  );

  const recalculateBomParentPrices = (contract) => {
    if (!contract?.contractItems) {
      return contract;
    }
    const updatedContractItems = contract.contractItems.map((item) => {
      if (
        item.articleType !== 'SALES_BILL_OF_MATERIAL' ||
        !item.salesBillOfMaterialItems
      ) {
        return item;
      }

      let newParentUnitPrice = item.unchangedUnitPrice
        ? Number(item.unchangedUnitPrice)
        : 0;

      if (item.useSalesBillOfMaterialItemPrices !== false) {
        const subItemsTotal = item.salesBillOfMaterialItems.reduce(
          (sum, subItem) => {
            const subPrice = Number(subItem.unitPrice) || 0;
            const subFactor = Number(subItem.articleFactor) || 1;
            return sum + subPrice * subFactor;
          },
          0,
        );
        newParentUnitPrice += subItemsTotal;
      }

      return {
        ...item,
        unitPrice: newParentUnitPrice,
      };
    });

    return { ...contract, contractItems: updatedContractItems };
  };

  const handleContractUpdate = useCallback(
    async (updatedContractData, editedItemInfo) => {
      if (!editedItemInfo) {
        setContract(updatedContractData);
        return;
      }
      const { uuid, isSubItem } = editedItemInfo;
      const editedItemIndex = updatedContractData.contractItems.findIndex(
        (item) => item.uuid === uuid,
      );
      const parentOfEditedSubItemIndex = isSubItem
        ? updatedContractData.contractItems.findIndex((item) =>
            item.salesBillOfMaterialItems?.some((sub) => sub.uuid === uuid),
          )
        : -1;
      if (isSubItem) {
        const contractWithRecalculatedParents =
          recalculateBomParentPrices(updatedContractData);
        setContract(contractWithRecalculatedParents);
        return;
      }
      const editedItem = updatedContractData.contractItems[editedItemIndex];
      if (editedItem?.articleType !== 'SALES_BILL_OF_MATERIAL') {
        setContract(updatedContractData);
        return;
      }
      if (editedItem?.articleType === 'SALES_BILL_OF_MATERIAL') {
        setIsPricingLoading(true);
        const reparentPricedItem = await fetchAndApplyPricesForBomItem(
          editedItem,
          customerId,
          contracts,
          fetchWeclappPrice,
        );
        const finalContractItems = [...updatedContractData.contractItems];
        finalContractItems[editedItemIndex] = reparentPricedItem;
        setContract({
          ...updatedContractData,
          contractItems: finalContractItems,
        });
        setIsPricingLoading(false);
      }
    },
    [customerId, contracts],
  );

  const totalContractVolume = useMemo(() => {
    if (
      !contract ||
      !contract.contractItems ||
      contract.contractItems.length === 0
    ) {
      return 0;
    }
    const { rows } = calculateAndEnrichItems(
      contract.contractItems,
      currentSource,
      new Date(),
    );
    const total = Number(
      rows.reduce((sum, item) => sum + (item.totalPrice || 0), 0),
    );
    return total;
  }, [contract, currentSource]);

  useEffect(() => {
    const loadContractData = async () => {
      setIsLoading(true);
      try {
        const { data: contractData } = await api.get(`/contract/${id}`);
        const { data: partyData } = await api.get(
          `/party/${contractData?.partyId}`,
        );

        const [datasourcesRes, allContractsRes, partyDatasourcesRes] =
          await Promise.all([
            api.get(
              `/datasource?source=msp&sourceId=${partyData?.customerNumber}`,
            ),
            api.get(`/contract?partyId=${contractData?.partyId}`),
            api.get(`/datasource?recordId=${partyData._id}&source=weclapp`),
          ]);

        const weclappCustomerId =
          partyDatasourcesRes.data?.result?.[0]?.sourceId;
        if (!weclappCustomerId) {
          toast.error('Keine Weclapp-Verknüpfung für diesen Kunden gefunden.');
          setIsLoading(false);
          return;
        }

        const contractWithPrices = await fetchAndApplyWeclappPrices(
          contractData,
          weclappCustomerId,
        );

        setParty(partyData);
        setCustomerId(weclappCustomerId);
        setAllContracts(allContractsRes.data?.result || []);
        const sortedDatasources = (datasourcesRes.data?.result || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setDatasources(sortedDatasources);
        if (sortedDatasources.length > 0) {
          setDatasource(sortedDatasources[0]._id);
        }
        setContract(contractWithPrices);
        setContractKey(JSON.stringify(contractWithPrices));
      } catch (error) {
        toast.error('Fehler beim Laden der Vertragsdaten:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContractData();
  }, [id, contracts]);

  useEffect(() => {
    if (!currentSource || currentSource.length === 0) {
      setCoverageStatus({ status: 'ok', messages: [] });
      return;
    }
    if (!contract?._id) {
      setCoverageStatus({ status: 'loading', messages: [] });
      return;
    }

    const contractsForCheck = [
      contract,
      ...allContracts.filter((c) => c._id !== contract._id),
    ];

    if (contractsForCheck.length === 0) {
      setCoverageStatus({
        status: 'warning',
        messages: [
          'Keine Verträge für diesen Kunden gefunden, um die Quelle abzudecken.',
        ],
      });
      return;
    }

    const allItems = contractsForCheck.flatMap((c) => c.contractItems || []);

    const { coverageStatus: newCoverageStatus } = calculateAndEnrichItems(
      allItems,
      currentSource,
      new Date(),
    );

    setCoverageStatus(newCoverageStatus);
  }, [allContracts, contract, currentSource]);

  const saveContract = async (updatedContract, updateKeyAfterSave = false) => {
    const toastId = toast.loading('Vertrag wird aktualisiert...', {
      closeButton: true,
    });
    const progressReporter = {
      report: (message) => {
        toast.update(toastId, {
          render: message,
          isLoading: true,
          closeButton: true,
        });
      },
      success: (message) => {
        toast.update(toastId, {
          render: message,
          type: 'success',
          isLoading: false,
          autoClose: 4000,
          closeButton: true,
        });
      },
      error: (message) => {
        toast.update(toastId, {
          render: message,
          type: 'error',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      },
    };
    try {
      updatedContract = {
        ...updatedContract,
        contractItems: updatedContract?.contractItems.map((item) => ({
          uuid: item.uuid,
          title: item?.title,
          description: item?.description,
          articleId: item?.articleId,
          articleNumber: item?.articleNumber,
          quantity: item?.quantity,
          articleFactor: item?.articleFactor,
          factor: item?.factor,
          rounding: item?.rounding,
          unitPrice: Number(item?.unitPrice),
          unchangedUnitPrice: Number(item?.unchangedUnitPrice || 0),
          type: item?.type,
          pricingDate: item?.pricingDate,
          discountPercentage: Number(item?.discountPercentage || 0),
          startDate: item?.startDate,
          endDate: item?.endDate,
          articleType: item?.articleType,
          groupName: item?.groupName,
          useSalesBillOfMaterialItemPrices:
            item?.useSalesBillOfMaterialItemPrices,
          lastCancellationNotificationDate:
            item?.lastCancellationNotificationDate || null,
          cancellationNotificationQuantity:
            item?.cancellationNotificationQuantity || undefined,
          cancellationNotificationUnit:
            item?.cancellationNotificationUnit || undefined,
          cancellationPeriodQuantity:
            item?.cancellationPeriodQuantity || undefined,
          cancellationPeriodUnit: item?.cancellationPeriodUnit || undefined,
          extensionQuantity: item?.extensionQuantity || undefined,
          extensionUnit: item?.extensionUnit || undefined,

          cancellationNotification: item?.cancellationNotification ?? undefined,
          salesBillOfMaterialItems: (item?.salesBillOfMaterialItems || []).map(
            (sub) => ({
              uuid: sub.uuid,
              title: sub?.title,
              description: sub?.description,
              articleId: sub?.articleId,
              articleNumber: sub?.articleNumber,
              quantity: sub?.quantity,
              articleFactor: sub?.articleFactor,
              unitPrice: sub?.unitPrice,
              pricingDate: sub?.pricingDate,
              discountPercentage: Number(sub?.discountPercentage || 0),
              type: sub?.type,
            }),
          ),
        })),
      };
      delete updatedContract?.version;
      await api.put(`/contract/${id}`, updatedContract);

      if (updateKeyAfterSave) {
        setContractKey(JSON.stringify(contract));
      }

      progressReporter.success('Vertrag erfolgreich aktualisiert.');
    } catch (error) {
      progressReporter.error('Fehler beim Aktualisieren des Vertrags.');
      console.error('Fehler beim Aktualisieren des Vertrags:', error);
      return;
    }
  };

  const createSalesInvoice = async () => {
    setShowInvoiceWarning(null);

    const toastId = toast.loading('Rechnung wird erstellt...', {
      closeButton: true,
    });
    const progressReporter = {
      report: (message) => {
        toast.update(toastId, {
          render: message,
          isLoading: true,
          closeButton: true,
        });
      },
      success: (message) => {
        toast.update(toastId, {
          render: message,
          type: 'success',
          isLoading: false,
          autoClose: 4000,
          closeButton: true,
        });
      },
      error: (message) => {
        toast.update(toastId, {
          render: message,
          type: 'error',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      },
    };

    try {
      progressReporter.report('Kundenverknüpfung wird geladen...');
      const { rows } = calculateAndEnrichItems(
        contract.contractItems,
        currentSource,
        new Date(),
      );
      progressReporter.report('Anfrage an Weclapp wird gestellt...');

      const getGroupeNameByUuid = (uuid) => {
        const group = contract.groupNames?.find((g) => g.uuid === uuid);
        return group ? group.title : null;
      };
      const salesInvoiceItems = rows
        .filter((item) => item.effectiveQuantity > 0)
        .map((item, index) => {
          return {
            positionNumber: index + 1,
            articleId: item.articleId,
            quantity: item.effectiveQuantity,
            manualUnitPrice: true,
            ...(item.groupName && {
              groupName: getGroupeNameByUuid(item.groupName),
            }),
            unitPrice: item?.unchangedUnitPrice ?? item.unitPrice.toFixed(5),
            ...(item.discountPercentage !== 0 && {
              discountPercentage: Math.abs(item.discountPercentage),
            }),
          };
        });

      const { data: createdSalesInvoice } = await proxy.post(
        `${contracts?.interfaces?.weclapp}/salesInvoice`,
        {
          customerId: customerId,
          orderNumberAtCustomer: contract?._id ?? undefined,
          salesInvoiceItems,
          tags: contracts?.weclappSalesInvoiceTags ?? [],
        },
      );

      const parentItems = createdSalesInvoice.salesInvoiceItems
        .filter((item) => !item.parentItemId)
        .map((item) => {
          const contractPosition = rows[item.positionNumber - 1];
          return {
            ...item,
            ...(contractPosition.groupName && {
              groupName: getGroupeNameByUuid(contractPosition.groupName),
            }),
          };
        });

      const subItems = parentItems.flatMap((item) => {
        const contractPosition = rows[item.positionNumber - 1];
        if (
          contractPosition.articleType === 'SALES_BILL_OF_MATERIAL' &&
          contractPosition.salesBillOfMaterialItems
        ) {
          return contractPosition.salesBillOfMaterialItems
            .filter((subItem) => subItem.displayQuantity > 0)
            .map((subItem) => {
              return {
                parentItemId: item.id,
                articleId: subItem.articleId,
                quantity: subItem.displayQuantity,
                ...(subItem.discountPercentage && {
                  discountPercentage: -subItem.discountPercentage,
                }),
                ...(contractPosition.useSalesBillOfMaterialItemPrices !==
                  false && {
                  manualUnitPrice: true,
                  unitPrice: subItem.unitPrice,
                }),
              };
            });
        }
        return [];
      });

      const updatedSalesInvoiceBody = {
        salesInvoiceItems: [...parentItems, ...subItems],
      };

      await proxy.put(
        `${contracts?.interfaces?.weclapp}/salesInvoice/id/${createdSalesInvoice.id}?ignoreMissingProperties=true`,
        updatedSalesInvoiceBody,
      );

      progressReporter.report(
        `Datum der letzten Abrechnung wird auf ${new Date().toLocaleDateString()} gesetzt.`,
      );
      delete contract?.version;
      const updatedContract = { ...contract, lastInvoicedAt: new Date() };
      await api.put(`/contract/${contract._id}`, updatedContract);

      // Update datasource articles' billedInContracts
      progressReporter.report(
        `Positionen auf der Eingangsrechnung werden aktualisiert...`,
      );
      const currentDatasourceObj = datasources.find(
        (d) => d._id === datasource,
      );
      if (
        currentDatasourceObj &&
        currentDatasourceObj.lastQueryResult?.articles
      ) {
        const updatedArticles =
          currentDatasourceObj.lastQueryResult.articles.map((sourceArticle) => {
            let totalUsageForThisSource = 0;
            let matchFound = false;
            const hasMatchingIdentifier = (qtyObject) => {
              return (
                qtyObject &&
                typeof qtyObject === 'object' &&
                Array.isArray(qtyObject.identifiers) &&
                qtyObject.identifiers.includes(sourceArticle.articleNumber)
              );
            };
            rows.forEach((row) => {
              const valueToAdd = row.effectiveQuantity / (row.factor ?? 1) || 0;
              if (hasMatchingIdentifier(row.quantity)) {
                totalUsageForThisSource =
                  totalUsageForThisSource + valueToAdd > sourceArticle.quantity
                    ? sourceArticle.quantity
                    : totalUsageForThisSource + valueToAdd;
                matchFound = true;
              }
              if (
                row.salesBillOfMaterialItems &&
                Array.isArray(row.salesBillOfMaterialItems)
              ) {
                row.salesBillOfMaterialItems.forEach((subItem) => {
                  const valueToAddForSubItem =
                    subItem.displayQuantity / (subItem.factor ?? 1) || 0;
                  if (hasMatchingIdentifier(subItem.quantity)) {
                    totalUsageForThisSource =
                      totalUsageForThisSource + valueToAddForSubItem >
                      sourceArticle.quantity
                        ? sourceArticle.quantity
                        : totalUsageForThisSource + valueToAddForSubItem;
                    matchFound = true;
                  }
                });
              }
            });
            if (matchFound && totalUsageForThisSource > 0) {
              const usageRecord = {
                contractId: contract._id,
                quantity: totalUsageForThisSource,
              };
              const existingUsages = sourceArticle.billedInContracts || [];
              return {
                ...sourceArticle,
                billedInContracts: [...existingUsages, usageRecord],
              };
            }
            return sourceArticle;
          });
        let totalSourceQuantity = 0;
        let totalUsedQuantityCapped = 0;
        updatedArticles.forEach((art) => {
          const sourceQty = Number(art.quantity) || 0;
          totalSourceQuantity += sourceQty;
          const billedSum = (art.billedInContracts || []).reduce(
            (sum, contractRef) => sum + (Number(contractRef.quantity) || 0),
            0,
          );
          const effectiveUsage = billedSum > sourceQty ? sourceQty : billedSum;
          totalUsedQuantityCapped += effectiveUsage;
        });
        const percentage =
          totalSourceQuantity > 0
            ? (totalUsedQuantityCapped / totalSourceQuantity) * 100
            : 0;
        const updatedDatasourcePayload = {
          ...currentDatasourceObj,
          lastQueryResult: {
            ...currentDatasourceObj.lastQueryResult,
            articles: updatedArticles,
            total: totalSourceQuantity,
            used: totalUsedQuantityCapped,
            percentage: Number(percentage.toFixed(2)),
          },
        };
        await api.put(`/datasource/${datasource}`, updatedDatasourcePayload);
        setDatasources((prev) =>
          prev.map((d) =>
            d._id === datasource ? updatedDatasourcePayload : d,
          ),
        );
      }

      setContract(updatedContract);
      progressReporter.success('Rechnung erfolgreich erstellt.');
    } catch (error) {
      progressReporter.error('Fehler beim Erstellen der Rechnung.');
      console.error('Fehler beim Erstellen der Rechnung:', error);
      return;
    }
  };

  const checkInvoiceEligibility = () => {
    if (!contract) {
      return;
    }
    if (contract.billingFrequency === 'no_billing') {
      setShowInvoiceWarning('no_billing');
      return;
    }
    if (!contract.lastInvoicedAt) {
      createSalesInvoice();
      return;
    }
    const lastInvoiced = dayjs(contract.lastInvoicedAt);
    const frequency = contract.billingFrequency;
    let nextPossibleInvoiceDate;
    if (
      frequency === 'jährlich' ||
      frequency === 'YEARLY' ||
      frequency === 'P1Y'
    ) {
      nextPossibleInvoiceDate = lastInvoiced.add(1, 'year').add(3, 'day');
    } else {
      nextPossibleInvoiceDate = lastInvoiced.add(1, 'month').add(3, 'day');
    }
    if (dayjs().isBefore(nextPossibleInvoiceDate, 'day')) {
      setShowInvoiceWarning('recent');
    } else {
      createSalesInvoice();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '85%',
        maxWidth: '100%',
        minHeight: '90vh',
        p: 2,
        alignItems: 'center',
        alignSelf: 'center',
        justifySelf: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Button
          variant="outlined"
          sx={{
            bgcolor: (theme) => theme.palette.background.paper,
          }}
          startIcon={<ArrowBack />}
          onClick={() =>
            JSON.stringify(contract) !== contractKey
              ? setShowUnsavedChangesWarning(true)
              : navigator('/billing/contracts')
          }
        >
          Zurück
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <FormControl sx={{ m: 1, minWidth: 270 }} size="small">
          <InputLabel id="datasource-label">Quellversion</InputLabel>
          <Select
            labelId="datasource-label"
            value={datasource || ''}
            label="Quellversion"
            onChange={(event) => setDatasource(event.target.value)}
            disabled={isLoading}
            sx={{ height: 37 }}
          >
            {datasources &&
              datasources?.slice(0, 10)?.map((doc) => (
                <MenuItem key={doc._id} value={doc._id}>
                  {dayjs(doc.createdAt).format('DD.MM.YYYY HH:mm')} Uhr
                </MenuItem>
              ))}
            {!datasources || datasources.length === 0 ? (
              <MenuItem value="" disabled>
                Keine Quellversionen verfügbar
              </MenuItem>
            ) : null}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          sx={{
            bgcolor: (theme) => theme.palette.background.paper,
          }}
          startIcon={<DocumentScanner />}
          onClick={checkInvoiceEligibility}
          disabled={!contract || !party || isPricingLoading}
        >
          Abrechnen
        </Button>
      </Box>
      <ContractTitle
        contract={contract}
        party={party}
        isLoading={isLoading}
        coverageStatus={coverageStatus}
        totalContractVolume={totalContractVolume}
        onContractUpdate={async (updatedData) => {
          setContract(updatedData);
          await saveContract(updatedData);
        }}
      />
      <ContractContent
        contract={contract}
        onContractChange={handleContractUpdate}
        saveContract={saveContract}
        source={currentSource}
        isLoading={isLoading}
        isPricingLoading={isPricingLoading}
        hasContractChanged={contractKey !== JSON.stringify(contract)}
        customerId={customerId}
      />

      <Dialog
        open={Boolean(showInvoiceWarning)}
        onClose={() => setShowInvoiceWarning(null)}
      >
        {showInvoiceWarning === 'no_billing' ? (
          <>
            <DialogTitle>Vertrag nicht zur Abrechnung vorgesehen</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Dieser Vertrag ist als "Keine Abrechnung" markiert und dient
                lediglich zum Halten des Vertrags. Möchten Sie trotzdem eine
                neue Rechnung erstellen?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                color="inherit"
                onClick={() => setShowInvoiceWarning(null)}
              >
                Abbrechen
              </Button>
              <Button onClick={createSalesInvoice} color="primary" autoFocus>
                Trotzdem abrechnen
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Vertrag bereits abgerechnet</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Dieser Vertrag wurde zuletzt am{' '}
                {dayjs(contract?.lastInvoicedAt).format('DD.MM.YYYY')}{' '}
                abgerechnet. Das Abrechnungsintervall (
                {contract?.billingFrequency}) ist seitdem noch nicht vergangen.
                Möchten Sie trotzdem eine neue Rechnung erstellen?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                color="inherit"
                onClick={() => setShowInvoiceWarning(null)}
              >
                Abbrechen
              </Button>
              <Button onClick={createSalesInvoice} color="primary" autoFocus>
                Trotzdem abrechnen
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={showUnsavedChangesWarning}
        onClose={() => setShowUnsavedChangesWarning(false)}
      >
        <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Es gibt ungespeicherte Änderungen. Möchten Sie die Änderungen
            verwerfen oder speichern?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => {
              setShowUnsavedChangesWarning(false);
            }}
          >
            Abbrechen
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <HoldButton
            countDown={0.5}
            countMessage="Bitte halten Sie die Taste länger gedrückt"
            color="error"
            variant="outlined"
            onClick={async () => {
              setShowUnsavedChangesWarning(false);
              navigator('/billing/contracts');
            }}
          >
            Verwerfen
          </HoldButton>
          <Button
            variant="outlined"
            color="primary"
            autoFocus
            onClick={async () => {
              await saveContract(contract);
              setShowUnsavedChangesWarning(false);
              navigator('/billing/contracts');
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
